import { ReactNode } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { Spacer } from '@vinted/web-ui'
import classNames from 'classnames'

import Advertisement from 'components/Advertisement'
import AdvertisementBlockCheck from 'components/AdvertisementBlockCheck'
import AppBanner from 'components/AppBanner'
import DomainSelectModal from 'components/DomainSelectModal'
import { GoogleAnalyticsTracker } from 'components/Google/Analytics'
import Header from 'components/Header'
import HeaderNoNavigation from 'components/HeaderNoNavigation'
import InAppMessage from 'components/InAppMessage'
import MainFooter from 'components/MainFooter'
import PolicyFooter from 'components/PolicyFooter'
import VacationNotification from 'components/VacationNotification'
import AdsProvider from 'containers/AdsProvider'
import GlobalTwoFaModal from 'components/GlobalTwoFAModal'
import ErrorBoundary from 'components/ErrorBoundary'

import { AdShape } from 'constants/ads'
import { Screen } from 'constants/tracking/screens'
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors'
import { getScreenName } from 'state/screen/selectors'
import { getIsContentOnlyView } from 'state/session/selectors'
import { getSystemConfiguration } from 'state/system-configuration/selectors'
import { CatalogDto, CountryLinkDto, LanguageDto } from 'types/dtos'
import { SearchBarSearchType } from 'constants/search'

import AdScripts from '../../components/AdScripts'
import { withLayout } from '../../libs/server-utils/hoc'

// TODO: figure out if we want to stick with using tracking screen names for logic
const policyFooterScreens = [
  Screen.ItemUpload,
  Screen.MessageList,
  Screen.MessageNew,
  Screen.MessageReply,
  Screen.PhoneVerification,
  Screen.Checkout,
]
const inboxScreens = [Screen.MessageList, Screen.MessageNew, Screen.MessageReply]
const noSpacerBeforeMainFooterScreens = [
  Screen.Sustainability,
  Screen.BusinessHomepage,
  Screen.About,
  Screen.HowItWorks,
]

export type Props = {
  languages: Array<LanguageDto>
  catalogTree: Array<CatalogDto>
  isWhiteBackground?: boolean
  shouldBreakoutLayout?: boolean
  hasNoNavigation: boolean
  domainLinks: Array<CountryLinkDto> | null
  userAgent?: string
  children: ReactNode
}

const resolveHeaderSearchType = (pathname: string): SearchBarSearchType => {
  const FIRST_VALUE_AFTER_SLASH_INDEX = 1
  const firstValueAfterSlash = pathname.split('/')[FIRST_VALUE_AFTER_SLASH_INDEX]

  if (firstValueAfterSlash === 'forum') return SearchBarSearchType.Forum
  if (firstValueAfterSlash === 'member') return SearchBarSearchType.User
  if (firstValueAfterSlash === 'help') return SearchBarSearchType.Faq

  return SearchBarSearchType.Item
}

export const FullPageLayout = ({
  languages,
  catalogTree,
  shouldBreakoutLayout,
  hasNoNavigation,
  domainLinks,
  userAgent,
  children,
  isWhiteBackground = true,
}: Props) => {
  const systemConfiguration = useSelector(getSystemConfiguration)
  const isWebGa4AnalyticsEnabled = useSelector(getIsFeatureSwitchEnabled('web_ga4_analytics'))
  const isContentOnly = useSelector(getIsContentOnlyView)
  const screenName = useSelector(getScreenName)
  const router = useRouter()

  if (!systemConfiguration) return null

  const renderHeader = () => {
    if (isContentOnly) return null

    if (hasNoNavigation) {
      return <HeaderNoNavigation portal={systemConfiguration.portal} />
    }

    return (
      <Header
        languages={languages}
        catalogTree={catalogTree}
        searchType={resolveHeaderSearchType(router.pathname)}
        // TODO: we should get isInCatalog value from the link same as it done in the BE https://github.com/vinted/core/blob/fb15dcd53ab6fd1dee5c46de2d07f55bd44b83a3/app/helpers/catalog_helper.rb#L29-L31
        isInCatalog={false}
        portal={systemConfiguration.portal}
        impressumUrl={systemConfiguration.urls.impressum}
        isBusinessAccountLinksVisible={systemConfiguration.businessAccountInformationalLinksVisible}
        isOurPlatformVisible={!!systemConfiguration.urls.ourPlatform}
      />
    )
  }

  const renderFooter = () => {
    if (isContentOnly) return null

    const isInPolicyFooterScreen = policyFooterScreens.includes(screenName)

    if (!isInPolicyFooterScreen && !hasNoNavigation) {
      return (
        <>
          {!noSpacerBeforeMainFooterScreens.includes(screenName) && (
            <Spacer size={Spacer.Size.X5Large} />
          )}
          <div className="u-ui-margin-top-auto">
            <MainFooter
              isBusinessInfoLinksEnabled={
                systemConfiguration.businessAccountInformationalLinksVisible
              }
              isDigitalLawEnabled={!!systemConfiguration.urls.ourPlatform}
              onlineComplaintUrl={systemConfiguration.urls.onlineComplaints}
            />
          </div>
        </>
      )
    }

    const isInInboxScreen = inboxScreens.includes(screenName)

    return (
      <PolicyFooter
        isDigitalLawEnabled={!!systemConfiguration.urls.ourPlatform}
        isBusinessInfoLinksEnabled={systemConfiguration.businessAccountInformationalLinksVisible}
        onlineComplaintUrl={systemConfiguration.urls.onlineComplaints}
        isStatic={isInInboxScreen}
        noDivider={isInInboxScreen}
      />
    )
  }

  return (
    <AdsProvider>
      {/*
        It is important to keep `AdScripts` above
        `Advertisement` component to initialize various
        third party scripts like googletag or etc.
        that are necessary for `Advertisement` component
        to actually render an advertisement.
      */}
      <AdScripts />

      <div
        className={classNames('fullpage-layout is-wide', {
          'u-background-white': isWhiteBackground,
        })}
      >
        <InAppMessage />
        {domainLinks && <DomainSelectModal domainLinks={domainLinks} />}
        {renderHeader()}

        <main className="site">
          <div className="site-wrapper">
            <ErrorBoundary FallbackComponent={ErrorBoundary.LayoutError}>
              <Advertisement shape={AdShape.Leaderboard} />

              <section className="site-content">
                <div className={classNames({ container: !shouldBreakoutLayout })}>
                  <div className={classNames({ container: shouldBreakoutLayout })}>
                    <VacationNotification />
                  </div>

                  <div className="row u-position-relative">
                    <section id="content" className="site-content__content-section grid12">
                      <div className="body-content">{children}</div>
                    </section>
                  </div>
                </div>
              </section>

              <AppBanner userAgent={userAgent} />
              <AdvertisementBlockCheck />
              {isWebGa4AnalyticsEnabled && <GoogleAnalyticsTracker />}
            </ErrorBoundary>
          </div>
        </main>

        {renderFooter()}
        <GlobalTwoFaModal />
      </div>
    </AdsProvider>
  )
}

export default withLayout<Props>(FullPageLayout)
